import React, { useState } from 'react';
import { Calendar, TrendingUp, Target, RefreshCw } from 'lucide-react';
import SwitchPlanModal from './SwitchPlanModal';
import { useRunPlan } from '../context/RunPlanContext';
import { useAnalytics } from '../context/AnalyticsContext';
import { REST_DAY_ICONS } from '../constants';

const PlanOverview = ({ selectedPlan, weeks, runs, darkMode, theme, onSwitchPlan, getStats, setSelectedDate, setShowAddRun }) => {
  const { restDayIcon } = useRunPlan();
  const { getStreakStats } = useAnalytics();
  const restIconConfig = REST_DAY_ICONS[restDayIcon] || REST_DAY_ICONS.sleep;
  const streakStats = getStreakStats();
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  // Calculate current week based on today's date
  const getCurrentWeek = () => {
    const today = new Date();
    for (let i = 0; i < weeks.length; i++) {
      const weekStart = weeks[i][0];
      const weekEnd = weeks[i][6];
      if (today >= weekStart && today <= weekEnd) {
        return i + 1;
      }
    }
    return 1; // Default to week 1
  };

  // Calculate overall stats
  const calculateOverallStats = () => {
    let totalCompleted = 0;
    let totalPlanned = 0;
    let runsCompleted = 0;
    let totalRuns = 0;

    weeks.forEach(week => {
      const stats = getStats(week);
      totalCompleted += stats.total;
      totalPlanned += stats.planned;

      week.forEach(day => {
        const run = runs[day.toDateString()];
        if (run && run.type !== 'cross') {
          totalRuns++;
          if (run.status === 'completed') runsCompleted++;
        }
      });
    });

    return {
      totalCompleted: totalCompleted.toFixed(1),
      totalPlanned: totalPlanned.toFixed(1),
      runsCompleted,
      totalRuns,
      percentComplete: totalRuns > 0 ? Math.round((runsCompleted / totalRuns) * 100) : 0
    };
  };

  // Get next week's runs
  const getNextWeekPreview = () => {
    const currentWeek = getCurrentWeek();
    if (currentWeek >= weeks.length) return null;

    const nextWeek = weeks[currentWeek]; // currentWeek is 1-indexed, array is 0-indexed
    const nextWeekRuns = [];

    nextWeek.forEach(day => {
      const run = runs[day.toDateString()];
      if (run) {
        nextWeekRuns.push({
          day: day.toLocaleDateString('en-US', { weekday: 'short' }),
          date: day.getDate(),
          ...run
        });
      }
    });

    return { weekNumber: currentWeek + 1, runs: nextWeekRuns };
  };

  const currentWeek = getCurrentWeek();
  const totalWeeks = weeks.length;
  const overallStats = calculateOverallStats();
  const nextWeek = getNextWeekPreview();
  const progressPercent = (currentWeek / totalWeeks) * 100;

  const runTypeEmojis = {
    easy: '🌱',
    recovery: '❤️‍🩹',
    cross: '🚴',
    workout: '💪',
    race: '🏁',
    long: '⏳'
  };

  // Get today's run
  const getTodaysRun = () => {
    const today = new Date();
    const todayString = today.toDateString();
    return runs[todayString];
  };

  // Get next race
  const getNextRace = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let nextRace = null;
    let nextRaceDate = null;
    let minDaysDiff = Infinity;

    Object.keys(runs).forEach(dateString => {
      const run = runs[dateString];
      if (run && run.type === 'race' && run.raceName) {
        const raceDate = new Date(dateString);
        raceDate.setHours(0, 0, 0, 0);

        const daysDiff = Math.ceil((raceDate - today) / (1000 * 60 * 60 * 24));

        // Only consider future races or today's race
        if (daysDiff >= 0 && daysDiff < minDaysDiff) {
          minDaysDiff = daysDiff;
          nextRace = run;
          nextRaceDate = raceDate;
        }
      }
    });

    if (nextRace && nextRaceDate) {
      return {
        name: nextRace.raceName,
        date: nextRaceDate,
        daysUntil: minDaysDiff,
        distance: nextRace.distance
      };
    }

    return null;
  };

  const todaysRun = getTodaysRun();
  const nextRace = getNextRace();

  return (
    <div className="p-4 pb-20">
      <div className="space-y-6">
        {/* Plan Header */}
        <div className={darkMode ? 'p-6 rounded-2xl border bg-slate-800 border-slate-700' : 'p-6 rounded-2xl border bg-white border-slate-200'}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold mb-1">{selectedPlan.name}</h2>
              <p className={darkMode ? 'text-sm text-slate-400' : 'text-sm text-slate-600'}>{selectedPlan.desc}</p>
            </div>
            <span className={'px-3 py-1 rounded-full text-xs font-medium ' + theme.light + ' ' + theme.lightText}>
              {selectedPlan.diff}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="mb-3">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Week {currentWeek} of {totalWeeks}</span>
              <span className={'text-sm font-bold ' + theme.text}>{Math.round(progressPercent)}%</span>
            </div>
            <div className={darkMode ? 'h-3 bg-slate-700 rounded-full overflow-hidden' : 'h-3 bg-slate-200 rounded-full overflow-hidden'}>
              <div
                className={'h-full rounded-full transition-all duration-500 ' + theme.primary}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          <button
            onClick={() => setShowSwitchModal(true)}
            className={darkMode ? 'text-sm text-slate-400 hover:text-white flex items-center gap-1 mt-2' : 'text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1 mt-2'}
          >
            <RefreshCw className="w-3 h-3" />
            Switch Plan
          </button>
        </div>

        {/* Today's Run & Streak */}
        <div className="grid grid-cols-2 gap-4">
          <div className={darkMode ? 'p-4 rounded-2xl border bg-slate-800 ' + (todaysRun ? (todaysRun.status === 'completed' ? 'border-green-500' : 'border-slate-700') : 'border-slate-700') : 'p-4 rounded-2xl border bg-white ' + (todaysRun ? (todaysRun.status === 'completed' ? 'border-green-500' : 'border-slate-200') : 'border-slate-200')}>
            <h3 className="text-sm font-medium mb-3 text-slate-500">Today</h3>
            {todaysRun && todaysRun.status === 'rest' ? (
              <div className="flex flex-col items-center text-center">
                <div className="text-3xl mb-1">{restIconConfig.icon}</div>
                <div className="font-semibold text-sm">Rest Day</div>
                <div className={darkMode ? 'text-xs text-slate-400' : 'text-xs text-slate-500'}>Recovery</div>
              </div>
            ) : todaysRun ? (
              <div className="flex flex-col items-center text-center">
                <div className="text-3xl mb-1">{runTypeEmojis[todaysRun.type] || '🏃'}</div>
                <div className="font-bold text-lg">
                  {todaysRun.type === 'cross' ? 'Cross' : (
                    <>
                      {todaysRun.distance}<span className="text-xs font-normal ml-0.5">mi</span>
                    </>
                  )}
                </div>
                <div className={darkMode ? 'text-xs text-slate-400 capitalize' : 'text-xs text-slate-500 capitalize'}>
                  {todaysRun.status === 'completed' ? (
                    <span className="text-green-500">✓ Done</span>
                  ) : (
                    todaysRun.type
                  )}
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  const today = new Date();
                  setSelectedDate(today);
                  setShowAddRun(true);
                }}
                className={darkMode ? 'w-full text-center py-2 hover:bg-slate-700 rounded-xl transition-colors' : 'w-full text-center py-2 hover:bg-slate-50 rounded-xl transition-colors'}
              >
                <div className="text-3xl mb-1">🌟</div>
                <div className="font-semibold text-sm mb-1">Rest Day</div>
                <div className={darkMode ? 'text-xs text-slate-400' : 'text-xs text-slate-500'}>Tap to add</div>
              </button>
            )}
          </div>

          {/* Weekly Streak */}
          <div className={darkMode ? 'p-4 rounded-2xl border bg-slate-800 border-slate-700' : 'p-4 rounded-2xl border bg-white border-slate-200'}>
            <h3 className="text-sm font-medium mb-3 text-slate-500">Streak</h3>
            <div className="flex flex-col items-center text-center">
              <div className="text-3xl mb-1">🌰</div>
              <div className="font-bold text-lg">
                <span className={darkMode ? 'bg-gradient-to-br from-orange-500 to-red-500 bg-clip-text text-transparent' : 'bg-gradient-to-br from-orange-400 to-red-400 bg-clip-text text-transparent'}>
                  {streakStats.weekly}
                </span>
                <span className={darkMode ? 'text-xs font-normal text-slate-400 ml-1' : 'text-xs font-normal text-slate-500 ml-1'}>
                  week{streakStats.weekly !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Race Countdown */}
        {nextRace && (
          <div className={darkMode ? 'p-5 rounded-2xl border bg-slate-800 border-slate-700' : 'p-5 rounded-2xl border bg-white border-slate-200'}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">🏁</span>
                  <div>
                    <h3 className="font-semibold">{nextRace.name}</h3>
                    <p className={darkMode ? 'text-xs text-slate-400' : 'text-xs text-slate-500'}>
                      {nextRace.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      {nextRace.distance > 0 && ` • ${nextRace.distance} mi`}
                    </p>
                  </div>
                </div>
              </div>
              <div className="text-center">
                <div className={darkMode ? 'text-4xl font-bold bg-gradient-to-br from-yellow-400 to-orange-500 bg-clip-text text-transparent' : 'text-4xl font-bold bg-gradient-to-br from-yellow-500 to-orange-600 bg-clip-text text-transparent'}>
                  {nextRace.daysUntil}
                </div>
                <div className={darkMode ? 'text-xs text-slate-400' : 'text-xs text-slate-500'}>
                  {nextRace.daysUntil === 0 ? 'Today!' : nextRace.daysUntil === 1 ? 'day' : 'days'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Overall Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className={darkMode ? 'p-5 rounded-2xl border bg-slate-800 border-slate-700' : 'p-5 rounded-2xl border bg-white border-slate-200'}>
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className={'w-5 h-5 ' + theme.text} />
              <span className="text-sm font-medium">Total Mileage</span>
            </div>
            <div className="text-2xl font-bold">
              <span className={theme.text}>{overallStats.totalCompleted}</span>
              <span className={darkMode ? 'text-slate-500 text-lg font-normal' : 'text-slate-400 text-lg font-normal'}>/{overallStats.totalPlanned}mi</span>
            </div>
          </div>

          <div className={darkMode ? 'p-5 rounded-2xl border bg-slate-800 border-slate-700' : 'p-5 rounded-2xl border bg-white border-slate-200'}>
            <div className="flex items-center gap-2 mb-2">
              <Target className={'w-5 h-5 ' + theme.text} />
              <span className="text-sm font-medium">Runs Completed</span>
            </div>
            <div className="text-2xl font-bold">
              <span className={theme.text}>{overallStats.runsCompleted}</span>
              <span className={darkMode ? 'text-slate-500 text-lg font-normal' : 'text-slate-400 text-lg font-normal'}>/{overallStats.totalRuns}</span>
            </div>
            <div className="mt-1 text-xs text-slate-500">{overallStats.percentComplete}% complete</div>
          </div>
        </div>

        {/* Next Week Preview */}
        {nextWeek && nextWeek.runs.length > 0 && (
          <div className={darkMode ? 'p-5 rounded-2xl border bg-slate-800 border-slate-700' : 'p-5 rounded-2xl border bg-white border-slate-200'}>
            <div className="flex items-center gap-2 mb-4">
              <Calendar className={'w-5 h-5 ' + theme.text} />
              <h3 className="font-semibold">Next Week Preview - Week {nextWeek.weekNumber}</h3>
            </div>
            <div className="space-y-2">
              {nextWeek.runs.map((run, idx) => (
                <div
                  key={idx}
                  className={darkMode ? 'flex items-center justify-between p-3 rounded-xl bg-slate-700' : 'flex items-center justify-between p-3 rounded-xl bg-slate-50'}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{runTypeEmojis[run.type] || '🏃'}</span>
                    <div>
                      <div className="font-medium">{run.day}</div>
                      <div className={darkMode ? 'text-xs text-slate-400 capitalize' : 'text-xs text-slate-500 capitalize'}>
                        {run.type === 'cross' ? 'Cross Training' : run.type}
                      </div>
                    </div>
                  </div>
                  {run.distance > 0 && (
                    <div className="font-bold">
                      {run.distance} <span className="text-sm font-normal">mi</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Plan Details */}
        <div className={darkMode ? 'p-5 rounded-2xl border bg-slate-800 border-slate-700' : 'p-5 rounded-2xl border bg-white border-slate-200'}>
          <h3 className="font-semibold mb-3">Plan Details</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className={darkMode ? 'text-slate-400' : 'text-slate-600'}>Duration:</span>
              <span className="font-medium">{totalWeeks} weeks</span>
            </div>
            <div className="flex justify-between">
              <span className={darkMode ? 'text-slate-400' : 'text-slate-600'}>Difficulty:</span>
              <span className="font-medium">{selectedPlan.diff}</span>
            </div>
            <div className="flex justify-between">
              <span className={darkMode ? 'text-slate-400' : 'text-slate-600'}>Total Planned Miles:</span>
              <span className="font-medium">{overallStats.totalPlanned} mi</span>
            </div>
          </div>
        </div>
      </div>

      {/* Switch Plan Modal */}
      {showSwitchModal && (
        <SwitchPlanModal
          darkMode={darkMode}
          onClose={() => setShowSwitchModal(false)}
          onConfirm={(startDate) => {
            setShowSwitchModal(false);
            onSwitchPlan(startDate);
          }}
        />
      )}
    </div>
  );
};

export default PlanOverview;
